import React, { useContext } from "react";
import { DisplayMobileSidebarContext } from "../../appContexts/DisplayMobileSidebarContext";
import { DarkModeContext } from "../../appContexts/DarkModeContext";
import styled from "styled-components";
import { MobileNavigatorFC } from "./MobileNavigatorFC";
import { DarkOrLightModeSelectorFC } from "../aside/DarkOrLightModeSelectorFC";
import { AuthBtnFC } from "../../auth/AuthBtnFC";
import { useAuth0 } from "@auth0/auth0-react";

export const _MobileSidebarFC = () => {
  const { displayMobileSidebar, setDisplayMobileSidebar } = useContext(
    DisplayMobileSidebarContext,
  );
  const { darkMode } = useContext(DarkModeContext);
  const { isAuthenticated, user } = useAuth0();

  return (
    <>
      <Overlay
        $show={displayMobileSidebar}
        onClick={() => setDisplayMobileSidebar(false)}
      />
      <MobileSidebarSC $show={displayMobileSidebar} darkMode={darkMode}>
        <SidebarHeader>
          <LogoSection>
            <LogoText>Avante</LogoText>
            <LogoSubtext>Connected</LogoSubtext>
          </LogoSection>
          <CloseButton onClick={() => setDisplayMobileSidebar(false)}>
            ✕
          </CloseButton>
        </SidebarHeader>

        <MobileNavigatorFC />

        <SidebarFooter>
          <FooterActions>
            <AuthButtonWrapper>
              <AuthBtnFC />
            </AuthButtonWrapper>
            <DarkModeWrapper>
              <DarkOrLightModeSelectorFC />
            </DarkModeWrapper>
          </FooterActions>
          {isAuthenticated && user?.picture && (
            <UserProfileSection>
              <UserAvatar src={user.picture} alt={user.name} />
              <UserName>{user.name}</UserName>
            </UserProfileSection>
          )}
        </SidebarFooter>
      </MobileSidebarSC>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 999;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  pointer-events: ${({ $show }) => ($show ? "all" : "none")};
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media screen and (min-width: 769px) {
    display: none;
  }
`;

const MobileSidebarSC = styled.div`
  display: flex;
  flex-direction: column;
  width: 280px;
  height: 100vh;
  position: fixed;
  z-index: 1000;
  top: 0;
  left: 0;

  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.98) 100%
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  border-right: 1px solid rgba(44, 90, 160, 0.1);
  box-shadow: 8px 0 32px rgba(0, 0, 0, 0.1);

  transform: ${({ $show }) => ($show ? "translateX(0)" : "translateX(-100%)")};
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  @media screen and (min-width: 769px) {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 20px;
  border-bottom: 1px solid rgba(44, 90, 160, 0.1);
  background: linear-gradient(
    180deg,
    rgba(44, 90, 160, 0.05) 0%,
    transparent 100%
  );
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LogoText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #2c5aa0;
  letter-spacing: -0.5px;
  line-height: 1;
`;

const LogoSubtext = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: rgba(44, 90, 160, 0.6);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  line-height: 1;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(44, 90, 160, 0.1);
  color: #2c5aa0;
  border-radius: 12px;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(44, 90, 160, 0.2);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding: 20px;
  padding-bottom: 80px;
  border-top: 1px solid rgba(44, 90, 160, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const UserProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(
    135deg,
    rgba(44, 90, 160, 0.05) 0%,
    rgba(52, 152, 219, 0.03) 100%
  );
  border-radius: 12px;
`;

const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: cover;
  border: 2px solid rgba(44, 90, 160, 0.2);
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2c5aa0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const DarkModeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(
    135deg,
    rgba(44, 90, 160, 0.08) 0%,
    rgba(52, 152, 219, 0.05) 100%
  );

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(44, 90, 160, 0.15) 0%,
      rgba(52, 152, 219, 0.1) 100%
    );
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(44, 90, 160, 0.15);
  }

  svg {
    width: 24px;
    height: 24px;
    cursor: pointer;
  }
`;

const AuthButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  button {
    width: 100%;
    height: 42px;
    padding: 0 16px;
    border-radius: 12px;
    border: none;
    background: linear-gradient(
      135deg,
      rgba(44, 90, 160, 0.12) 0%,
      rgba(52, 152, 219, 0.08) 100%
    );
    color: #2c5aa0;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(44, 90, 160, 0.2) 0%,
        rgba(52, 152, 219, 0.15) 100%
      );
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(44, 90, 160, 0.2);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;
